import { createRef, useMemo, useState } from 'react';

import { useCreateTargetMutation } from '../../../../hooks/useCreateTargetMutation';
import { useMyUntargetedUsersQuery } from '../../../../hooks/useMyUnTargetedUsersQuery';

export type Direction = 'left' | 'right' | 'up' | 'down';

export const useTargetSwipe = (eventId: string) => {
  const { data: myUntargetedUsersData, loading: isUsersLoading } =
    useMyUntargetedUsersQuery(eventId);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const { createTarget } = useCreateTargetMutation();

  const cards = useMemo(
    () => myUntargetedUsersData?.myUntargetedUsers ?? [],
    [myUntargetedUsersData],
  );

  const childRefs = useMemo(
    () =>
      Array(cards.length)
        .fill(0)
        .map(() =>
          createRef<{
            swipe(dir?: Direction): Promise<void>;
            restoreCard(): Promise<void>;
          }>(),
        ),
    [cards],
  );

  const updateCurrentIndex = () => {
    setCurrentIndex((currentIndex) => currentIndex + 1);
  };

  const canSwipe = currentIndex >= 0 && currentIndex < cards.length;

  const swiped = (direction: Direction) => {
    updateCurrentIndex();
    setCurrentImageIndex(0);
    createTarget({
      eventId,
      isTargeted: direction === 'right',
      targetUserId: cards[currentIndex].id,
    });
  };

  const swipe = async (dir: Direction) => {
    if (canSwipe && currentIndex < cards.length) {
      await childRefs[childRefs.length - currentIndex - 1].current?.swipe(dir);
    }
  };

  const currentUser = useMemo(() => cards[currentIndex], [cards, currentIndex]);

  const numberOfImages = useMemo(
    () => (currentUser?.images ?? []).length,
    [currentUser],
  );

  const onNextImagePress = () => {
    setCurrentImageIndex((prev) => (prev + 1) % numberOfImages);
  };

  const onPreviousImagePress = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + numberOfImages) % numberOfImages,
    );
  };

  const hasSeenAllProfiles = useMemo(
    () => currentIndex >= cards.length,
    [currentIndex, cards],
  );

  return {
    swiped,
    swipe,
    canSwipe,
    cards,
    childRefs,
    onNextImagePress,
    onPreviousImagePress,
    currentImageIndex,
    numberOfImages,
    hasSeenAllProfiles,
    currentIndex,
    isUsersLoading,
  };
};
